import { ensureDirectLineOnline } from "../directLlineClient";
import { ensureDirectLineHealthy } from "../directLineHealth";
import { getCurrentMsalAccount } from "../utils/msalTokenService";
import { setBootstrapError, setConversationId, unlockChatInput } from "./chatSlice";
import {
  chatModuleState,
  currentSessionId,
  handledActivityIds,
  teardownActivitySubscription,
} from "./chatModuleState";
import { handleChatActivity } from "./chatActivityRouter";
import { initializeCoreAgentBootstrap } from "./chatProjectThunks";
import type { ChatDispatch } from "./chatSliceTypes";

const CONNECT_TIMEOUT_MS = 15000;

async function resetDirectLineClient(): Promise<void> {
  const { resetDirectLine } = await import("../directLlineClient");
  await resetDirectLine();
}

function logListenerEntry(): void {
  console.log("🎧 [LISTENER] initializeChatListener called");
  console.log(
    "🎧 [LISTENER] Current state - isListenerInitialized:",
    chatModuleState.isListenerInitialized,
    "sessionId:",
    currentSessionId
  );
  console.log(
    "🎧 [LISTENER] activitySubscription status:",
    chatModuleState.activitySubscription ? "exists" : "null"
  );
  console.log("🎧 [LISTENER] handledActivityIds count:", handledActivityIds.size);
}

/**
 * A page load (or HMR) re-evaluates this module and mints a new session ID,
 * while module state can survive; a changed ID therefore means the listener
 * state we're holding belongs to a previous page and must be dropped.
 */
async function resetOnSessionChange(): Promise<void> {
  const { lastSessionId, isListenerInitialized } = chatModuleState;
  if (lastSessionId === null || lastSessionId === currentSessionId || !isListenerInitialized) {
    return;
  }

  console.log("🎧 [LISTENER] Session ID changed - resetting listener state");
  chatModuleState.isListenerInitialized = false;
  handledActivityIds.clear();
  // Also reset DirectLine and conversation metadata
  await resetDirectLineClient();
}

/**
 * Step 1: bring DirectLine online. Returns null when the connection failed or
 * the switch was superseded while we waited — both mean "stop here".
 */
async function connectDirectLine(
  dispatch: ChatDispatch,
  switchSignal?: AbortSignal
): Promise<any> {
  try {
    console.log("🎧 [LISTENER] Step 1: Getting MSAL account and connecting to DirectLine");
    // Phase 1: MSAL account so DirectLine is initialized with identity
    const msalAccount = getCurrentMsalAccount();
    const directLine = await ensureDirectLineOnline(CONNECT_TIMEOUT_MS, null, msalAccount);

    if (switchSignal?.aborted) {
      console.log("🔒 [LISTENER] Superseded after ensureDirectLineOnline — cleaning up");
      await resetDirectLineClient();
      chatModuleState.isListenerInitialized = false;
      return null;
    }

    console.log("✅ [LISTENER] DirectLine connection established");
    return directLine;
  } catch (err) {
    console.error("❌ [LISTENER] Failed to establish DirectLine connection:", err);
    try {
      await resetDirectLineClient();
    } catch {
      // Best-effort teardown: the listener is being torn down anyway, so a
      // failure to reset the DirectLine client must not mask the original error.
    }
    // Reset flag so we can retry on subsequent attempts
    chatModuleState.isListenerInitialized = false;
    handledActivityIds.clear();
    dispatch(setBootstrapError("Unable to connect to the chat service. Please refresh and try again."));
    return null;
  }
}

/**
 * Step 2: durable activity subscription, established BEFORE the health probe so
 * it catches the health_ack. It stays active for the lifetime of the session.
 */
function subscribeToActivities(
  directLine: any,
  dispatch: ChatDispatch,
  getState: () => any
): void {
  const directLineUserId = directLine?.userId;
  console.log("🎧 [LISTENER] DirectLine userId:", directLineUserId);
  console.log("🎧 [LISTENER] Setting up activity subscription");

  chatModuleState.activitySubscription = directLine.activity$.subscribe({
    next: (activity: any) =>
      handleChatActivity(activity, directLineUserId, { dispatch, getState }),
    error: (err: any) => {
      console.warn(
        "⚠️ [LISTENER] Activity subscription error — connection lost, will reconnect on next send:",
        err
      );
      // Mark listener as uninitialized so reconnection can reinitialize it
      chatModuleState.isListenerInitialized = false;
      // Don't show a blocking error — the pre-send health check in sendMessage
      // will detect the unhealthy connection and transparently reconnect.
      dispatch(unlockChatInput());
    },
    complete: () => {
      dispatch(unlockChatInput());
    },
  });
}

/**
 * Step 3: verify the backend is ready. Returns false when the caller must stop —
 * either the probe failed or a newer project switch superseded this one.
 */
async function runHealthProbe(
  directLine: any,
  dispatch: ChatDispatch,
  switchSignal?: AbortSignal
): Promise<boolean> {
  console.log("🎧 [LISTENER] Step 3: Running health probe");
  try {
    await ensureDirectLineHealthy(directLine);
    console.log("✅ [LISTENER] Health probe successful");
    return true;
  } catch (err) {
    // If superseded by a newer project switch, exit silently (not an error)
    if (switchSignal?.aborted) {
      console.log("🔒 [LISTENER] Superseded during health probe — cleaning up silently");
      teardownActivitySubscription();
      return false;
    }

    console.error("❌ [LISTENER] Health probe failed:", err);
    teardownActivitySubscription();
    dispatch(setBootstrapError("DirectLine health check failed. Please refresh and try again."));
    return false;
  }
}

/** Step 4: bootstrap, now that the health probe confirms the backend is ready. */
function startBootstrap(dispatch: ChatDispatch, getState: () => any): void {
  console.log("🎧 [LISTENER] Step 4: Checking authentication state for bootstrap");
  const isAuthenticated = getState().userAuthAndAdminSlice?.isAuthenticated;
  console.log("🎧 [LISTENER] isAuthenticated:", isAuthenticated);

  if (isAuthenticated) {
    console.log("🎧 [LISTENER] Starting bootstrap initialization");
    dispatch(initializeCoreAgentBootstrap());
  } else {
    console.log("⚠️ [LISTENER] User not authenticated - skipping bootstrap");
  }

  console.log("✅ [LISTENER] initializeChatListener completed successfully");
  console.log(
    "✅ [LISTENER] Final state - isListenerInitialized:",
    chatModuleState.isListenerInitialized,
    "subscription active:",
    !!chatModuleState.activitySubscription
  );
}

/** Connect DirectLine, subscribe to its activities, probe health, bootstrap. */
export const initializeChatListener =
  (switchSignal?: AbortSignal) =>
  async (dispatch: any, getState: any): Promise<void> => {
    logListenerEntry();

    await resetOnSessionChange();
    chatModuleState.lastSessionId = currentSessionId;

    if (chatModuleState.isListenerInitialized) {
      console.log("⚠️ [LISTENER] Listener already initialized - returning early");
      console.log("⚠️ [LISTENER] If you need to reinitialize, call resetChatModuleState() first");
      return;
    }
    console.log("✅ [LISTENER] Guard check passed - proceeding with initialization");

    const directLine = await connectDirectLine(dispatch, switchSignal);
    if (!directLine) return;

    console.log("🎧 [LISTENER] Marking listener as initialized");
    chatModuleState.isListenerInitialized = true;

    // Capture and store conversation ID when available
    if (directLine.conversationId) {
      console.log("🎧 [LISTENER] DirectLine conversationId:", directLine.conversationId);
      dispatch(setConversationId(directLine.conversationId));
    }

    subscribeToActivities(directLine, dispatch, getState);

    const healthy = await runHealthProbe(directLine, dispatch, switchSignal);
    if (!healthy) return;

    // Check if superseded after health probe completed successfully
    if (switchSignal?.aborted) {
      console.log("🔒 [LISTENER] Superseded after health probe — cleaning up");
      teardownActivitySubscription();
      await resetDirectLineClient();
      return;
    }

    startBootstrap(dispatch, getState);
  };
